import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock, Mail, Phone, User } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { setCustomerToken, isCustomerAuthenticated } from '../lib/session';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

export default function AccountAuth() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = searchParams.get('next') || '/account';
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ full_name: '', email: '', phone: '', password: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isCustomerAuthenticated()) {
      navigate(next, { replace: true });
    }
  }, [navigate, next]);

  const copy = useMemo(() => (
    i18n.language === 'en'
      ? {
        title: 'My Account',
        subtitle: 'Register to book tours, pay deposits, and track booking status.',
        login: 'Sign In',
        register: 'Register',
        email: 'Email',
        password: 'Password',
        fullName: 'Full name',
        phone: 'Phone',
        continue: 'Continue',
        account: 'Go to Home',
      }
      : {
        title: 'Личный кабинет',
        subtitle: 'Зарегистрируйтесь, чтобы бронировать туры, оплачивать депозит и видеть статус брони.',
        login: 'Вход',
        register: 'Регистрация',
        email: 'Email',
        password: 'Пароль',
        fullName: 'Имя и фамилия',
        phone: 'Телефон',
        continue: 'Продолжить',
        account: 'На главную',
      }
  ), [i18n.language]);

  const onLogin = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await api.post('/customer/login', loginData);
      setCustomerToken(response.data.access_token);
      toast.success(i18n.language === 'en' ? 'Signed in' : 'Вход выполнен');
      navigate(next);
    } catch (error) {
      toast.error(error?.response?.data?.detail || (i18n.language === 'en' ? 'Failed to sign in' : 'Не удалось войти'));
    } finally {
      setLoading(false);
    }
  };

  const onRegister = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await api.post('/customer/register', registerData);
      setCustomerToken(response.data.access_token);
      toast.success(i18n.language === 'en' ? 'Account created' : 'Аккаунт создан');
      navigate(next);
    } catch (error) {
      toast.error(error?.response?.data?.detail || (i18n.language === 'en' ? 'Failed to register' : 'Не удалось зарегистрироваться'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-20 px-4 md:px-8">
      <div className="max-w-5xl mx-auto grid gap-8 lg:grid-cols-[0.95fr_1.05fr]">
        <div className="glass rounded-3xl p-8 md:p-10 space-y-5">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs text-white/60">
            <Lock size={14} />
            <span>AICHIN</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-black text-white">{copy.title}</h1>
          <p className="text-white/70">{copy.subtitle}</p>
          <Button asChild variant="outline" className="border-white/10 bg-white/5 text-white hover:bg-white/10 rounded-full">
            <Link to="/">{copy.account}</Link>
          </Button>
        </div>

        <div className="glass rounded-3xl p-8 md:p-10">
          <Tabs defaultValue="login" className="space-y-6">
            <TabsList className="grid w-full grid-cols-2 bg-white/5">
              <TabsTrigger value="login">{copy.login}</TabsTrigger>
              <TabsTrigger value="register">{copy.register}</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={onLogin} className="space-y-5">
                <div>
                  <Label className="text-white mb-2 block">{copy.email}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={16} />
                    <Input type="email" required value={loginData.email} onChange={(event) => setLoginData((prev) => ({ ...prev, email: event.target.value }))} className="pl-10 bg-zinc-900/50 border-white/10 text-white" />
                  </div>
                </div>
                <div>
                  <Label className="text-white mb-2 block">{copy.password}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={16} />
                    <Input type="password" required value={loginData.password} onChange={(event) => setLoginData((prev) => ({ ...prev, password: event.target.value }))} className="pl-10 bg-zinc-900/50 border-white/10 text-white" />
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[#E11D2E] hover:bg-[#E11D2E]/90 text-white rounded-full py-3">
                  {copy.continue}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={onRegister} className="space-y-5">
                <div>
                  <Label className="text-white mb-2 block">{copy.fullName}</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={16} />
                    <Input required value={registerData.full_name} onChange={(event) => setRegisterData((prev) => ({ ...prev, full_name: event.target.value }))} className="pl-10 bg-zinc-900/50 border-white/10 text-white" />
                  </div>
                </div>
                <div>
                  <Label className="text-white mb-2 block">{copy.email}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={16} />
                    <Input type="email" required value={registerData.email} onChange={(event) => setRegisterData((prev) => ({ ...prev, email: event.target.value }))} className="pl-10 bg-zinc-900/50 border-white/10 text-white" />
                  </div>
                </div>
                <div>
                  <Label className="text-white mb-2 block">{copy.phone}</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={16} />
                    <Input value={registerData.phone} onChange={(event) => setRegisterData((prev) => ({ ...prev, phone: event.target.value }))} className="pl-10 bg-zinc-900/50 border-white/10 text-white" />
                  </div>
                </div>
                <div>
                  <Label className="text-white mb-2 block">{copy.password}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={16} />
                    <Input type="password" required minLength={8} value={registerData.password} onChange={(event) => setRegisterData((prev) => ({ ...prev, password: event.target.value }))} className="pl-10 bg-zinc-900/50 border-white/10 text-white" />
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[#E11D2E] hover:bg-[#E11D2E]/90 text-white rounded-full py-3">
                  {copy.continue}
                </Button>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
